const MAX_RECURSION_DEPTH = 10;
/**
 * Sets the disabled attribute to true on this element and
 * recursively applies it to all its children up to a max depth.
 */
export function setDisabledAttribute(
  element: Element,
  operation: 'add' | 'remove' = 'add',
  depth: number = 0
): void {
  const attributeChange =
    operation === 'add'
      ? (i: number) =>
          element.children.item(i)?.setAttribute('disabled', 'true')
      : (i: number) => element.children.item(i)?.removeAttribute('disabled');

  for (let i = 0; i < element.children.length; i++) {
    const currentDepth = depth + 1;
    attributeChange(i);
    if (
      (element.children.item(i)?.children.length ?? 0) > 0 &&
      currentDepth < MAX_RECURSION_DEPTH
    ) {
      setDisabledAttribute(element.children.item(i)!, operation, currentDepth);
    }
  }
}

/**
 * Adds a class to this element and
 * recursively applies it to all its children up to a max depth.
 */
export function setClassAttribute(
  element: Element,
  classString: string,
  operation: 'add' | 'remove' = 'add',
  depth: number = 0
): void {
  const attributeChange =
    operation === 'add'
      ? (i: number, currentClass: string) =>
          element.children
            .item(i)
            ?.setAttribute('class', mergeClasses(currentClass, classString))
      : (i: number, currentClass: string) =>
          element.children
            .item(i)
            ?.setAttribute('class', removeClasses(currentClass, classString));

  for (let i = 0; i < element.children.length; i++) {
    const currentDepth = depth + 1;
    const currentClass = element.children.item(i)?.getAttribute('class') ?? '';
    attributeChange(i, currentClass);
    if (
      (element.children.item(i)?.children.length ?? 0) > 0 &&
      currentDepth < MAX_RECURSION_DEPTH
    ) {
      setClassAttribute(
        element.children.item(i)!,
        classString,
        operation,
        currentDepth
      );
    }
  }
}

export function mergeClasses(current: string, extra?: string): string {
  return extra ? `${current} ${extra}` : current;
}

export function removeClasses(current: string, extra?: string): string {
  return extra ? current.split(extra).join('') : current;
}
